import { useCallback, useEffect, useRef, useState } from "react";

import api from "../services/api";

// GET /hotspot/status -> { enabled, integrationId?, baseUrl?, tenant?: { id, nome, slug, modo }, error? }
// Cached in memory for 60 s so every consumer (menu item, /hotspot page) shares
// the same request instead of hitting the backend on every render/mount.
const CACHE_TTL_MS = 60000;

let cache = null; // { data, fetchedAt }
let inFlight = null; // Promise shared by concurrent callers

const emptyStatus = { enabled: false, tenant: null, error: null };

const fetchStatus = async (force) => {
  if (!force && cache && Date.now() - cache.fetchedAt < CACHE_TTL_MS) {
    return cache.data;
  }
  if (!force && inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const { data } = await api.get("/hotspot/status");
      const normalized = {
        enabled: !!data?.enabled,
        integrationId: data?.integrationId ?? null,
        baseUrl: data?.baseUrl ?? null,
        tenant: data?.tenant ?? null,
        error: data?.error ?? null,
      };
      cache = { data: normalized, fetchedAt: Date.now() };
      return normalized;
    } catch (err) {
      // Never throws to the caller: a failed request just means "not enabled".
      const normalized = {
        enabled: false,
        integrationId: null,
        baseUrl: null,
        tenant: null,
        error: err?.response?.data?.error || err?.message || "unavailable",
      };
      cache = { data: normalized, fetchedAt: Date.now() };
      return normalized;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};

const useHotspotStatus = () => {
  const [status, setStatus] = useState(() => cache?.data || emptyStatus);
  const [loading, setLoading] = useState(!cache);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const load = useCallback(async (force = false) => {
    if (isMountedRef.current) setLoading(true);
    const data = await fetchStatus(force);
    if (isMountedRef.current) {
      setStatus(data);
      setLoading(false);
    }
    return data;
  }, []);

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(() => load(true), [load]);

  return {
    enabled: status.enabled,
    integrationId: status.integrationId,
    baseUrl: status.baseUrl,
    tenant: status.tenant,
    error: status.error,
    loading,
    refresh,
  };
};

export default useHotspotStatus;
